/**
 * Image Processing Service
 *
 * Dynamic image resize with filesystem cache.
 * Separated from StorageService to keep concerns clean:
 * - StorageService: file CRUD, dedup, scopes
 * - ImageProcessingService: sharp transforms, cache management
 */

import { join } from 'node:path'
import { existsSync, mkdirSync } from 'node:fs'
import { readFile, writeFile, rm, mkdir } from 'node:fs/promises'
import { createHash } from 'node:crypto'
import sharp from 'sharp'
import type { Logger } from 'pino'

// ============================================================================
// TYPES
// ============================================================================

export interface ResizeOptions {
  /** Width in pixels (1-maxWidth) */
  w?: number
  /** Height in pixels (1-maxHeight) */
  h?: number
  /** Resize fit strategy */
  fit?: 'cover' | 'contain' | 'fill'
  /** JPEG/WebP quality (1-100) */
  q?: number
  /** Output format (auto = content negotiation from Accept header) */
  f?: 'jpg' | 'webp' | 'png' | 'avif' | 'auto'
  /** Device pixel ratio — multiplies w/h */
  dpr?: 1 | 2 | 3
}

export interface ResizeResult {
  buffer: Buffer
  mimetype: string
  format: string
  cacheHit: boolean
}

export interface ImageProcessingConfig {
  /** Max resize width (default: 2048) */
  maxWidth: number
  /** Max resize height (default: 2048) */
  maxHeight: number
  /** Default quality (default: 80) */
  defaultQuality: number
  /** Cache base directory */
  cachePath: string
}

let imageProcessingInstance: ImageProcessingService | null = null

/**
 * Gets the image processing service singleton
 */
export function getImageProcessingService(): ImageProcessingService {
  if (!imageProcessingInstance) {
    throw new Error('ImageProcessingService not initialized. Call initImageProcessingService() first.')
  }
  return imageProcessingInstance
}

/**
 * Initializes the image processing service singleton
 */
export function initImageProcessingService(config: ImageProcessingConfig, logger: Logger): ImageProcessingService {
  imageProcessingInstance = new ImageProcessingService(config, logger)
  return imageProcessingInstance
}

const FORMAT_TO_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  webp: 'image/webp',
  png: 'image/png',
  avif: 'image/avif'
}

const MIME_TO_FORMAT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/png': 'png',
  'image/avif': 'avif'
}

// ============================================================================
// SERVICE
// ============================================================================

export class ImageProcessingService {
  private config: ImageProcessingConfig
  private logger: Logger
  private cacheReady = false

  constructor(config: ImageProcessingConfig, logger: Logger) {
    this.config = config
    this.logger = logger.child({ service: 'image-processing' })
  }

  /**
   * Validates and normalizes resize options.
   * Returns null if no resize is needed (no w/h specified).
   */
  parseOptions(raw: Record<string, string | undefined>, acceptHeader?: string): ResizeOptions | null {
    const wRaw = raw['w'] ?? raw['width']
    const hRaw = raw['h'] ?? raw['height']
    const w = wRaw ? parseInt(wRaw, 10) : undefined
    const h = hRaw ? parseInt(hRaw, 10) : undefined

    // No resize params → serve original
    if (!w && !h) return null

    const dprRaw = raw['dpr']
    const dpr = dprRaw ? Math.min(3, Math.max(1, parseInt(dprRaw, 10) || 1)) as 1 | 2 | 3 : 1
    const fit = (['cover', 'contain', 'fill'].includes(raw['fit'] || '') ? raw['fit'] : 'cover') as ResizeOptions['fit']
    const qRaw = raw['q'] ?? raw['quality']
    const q = qRaw ? Math.min(100, Math.max(1, parseInt(qRaw, 10) || this.config.defaultQuality)) : this.config.defaultQuality

    // Resolve format (accepts 'f' or 'format', and 'jpeg' as alias of 'jpg')
    let f: ResizeOptions['f'] = 'auto'
    let fmtRaw = raw['f'] ?? raw['format']
    if (fmtRaw === 'jpeg') fmtRaw = 'jpg'
    if (fmtRaw && ['jpg', 'webp', 'png', 'avif'].includes(fmtRaw)) {
      f = fmtRaw as ResizeOptions['f']
    }

    // Apply DPR and clamp to max dimensions
    const effectiveW = w ? Math.min(w * dpr, this.config.maxWidth) : undefined
    const effectiveH = h ? Math.min(h * dpr, this.config.maxHeight) : undefined

    // Resolve auto format from Accept header
    if (f === 'auto') {
      f = this.resolveFormat(acceptHeader)
    }

    return { w: effectiveW, h: effectiveH, fit, q, f, dpr }
  }

  /**
   * Resizes an image buffer with caching.
   */
  async resize(fileId: string, buffer: Buffer, opts: ResizeOptions): Promise<ResizeResult> {
    const cacheKey = this.buildCacheKey(fileId, opts)
    const format = opts.f === 'auto' ? 'jpg' : opts.f!
    const cachePath = join(this.config.cachePath, fileId, `${cacheKey}.${format}`)

    // Check cache
    if (existsSync(cachePath)) {
      const cached = await readFile(cachePath)
      return {
        buffer: cached,
        mimetype: FORMAT_TO_MIME[format] || 'image/jpeg',
        format,
        cacheHit: true
      }
    }

    // Process with sharp
    let pipeline = sharp(buffer).resize(opts.w, opts.h, {
      fit: opts.fit || 'cover',
      position: 'center',
      withoutEnlargement: true
    })

    // Flatten alpha channel for formats that don't support transparency (JPEG)
    if (format === 'jpg') {
      pipeline = pipeline.flatten({ background: { r: 255, g: 255, b: 255 } })
    }

    // Apply format
    switch (format) {
      case 'webp':
        pipeline = pipeline.webp({ quality: opts.q || this.config.defaultQuality })
        break
      case 'png':
        pipeline = pipeline.png()
        break
      case 'avif':
        pipeline = pipeline.avif({ quality: opts.q || this.config.defaultQuality })
        break
      case 'jpg':
      default:
        pipeline = pipeline.jpeg({ quality: opts.q || this.config.defaultQuality })
        break
    }

    const result = await pipeline.toBuffer()

    // Write to cache (fire-and-forget, don't block response)
    this.writeCache(cachePath, result).catch(err => {
      this.logger.warn({ err, fileId, cacheKey }, 'Failed to write resize cache')
    })

    this.logger.debug({ fileId, w: opts.w, h: opts.h, format, size: result.length }, 'Image resized')

    return {
      buffer: result,
      mimetype: FORMAT_TO_MIME[format] || 'image/jpeg',
      format,
      cacheHit: false
    }
  }

  /**
   * Invalidates all cached resizes for a file.
   * Call this before deleting the original file.
   */
  async invalidateCache(fileId: string): Promise<void> {
    const cacheDir = join(this.config.cachePath, fileId)
    if (existsSync(cacheDir)) {
      await rm(cacheDir, { recursive: true, force: true })
      this.logger.debug({ fileId }, 'Resize cache invalidated')
    }
  }

  /**
   * Builds ETag for conditional requests.
   */
  buildETag(fileHash: string | undefined, opts: ResizeOptions): string {
    const key = this.buildCacheKey(fileHash || 'unknown', opts)
    return `"${key}"`
  }

  /**
   * Resolves best format from Accept header.
   */
  private resolveFormat(acceptHeader?: string): 'jpg' | 'webp' | 'png' | 'avif' {
    if (!acceptHeader) return 'jpg'
    if (acceptHeader.includes('image/avif')) return 'avif'
    if (acceptHeader.includes('image/webp')) return 'webp'
    return 'jpg'
  }

  /**
   * Builds a deterministic cache key from normalized options.
   */
  private buildCacheKey(fileId: string, opts: ResizeOptions): string {
    const normalized = JSON.stringify({
      w: opts.w,
      h: opts.h,
      fit: opts.fit || 'cover',
      q: opts.q || this.config.defaultQuality,
      f: opts.f === 'auto' ? 'jpg' : opts.f
    })
    return createHash('sha256').update(`${fileId}:${normalized}`).digest('hex').slice(0, 16)
  }

  /**
   * Writes a buffer to cache, creating directories as needed.
   */
  private async writeCache(cachePath: string, buffer: Buffer): Promise<void> {
    await this.ensureCacheDir()
    const dir = join(cachePath, '..')
    if (!existsSync(dir)) {
      await mkdir(dir, { recursive: true })
    }
    await writeFile(cachePath, buffer)
  }

  /**
   * Ensures the base cache directory exists (lazy init).
   */
  private async ensureCacheDir(): Promise<void> {
    if (this.cacheReady) return
    if (!existsSync(this.config.cachePath)) {
      mkdirSync(this.config.cachePath, { recursive: true })
    }
    this.cacheReady = true
  }

  /**
   * Gets the mimetype extension mapping for response headers.
   */
  static formatToMime(format: string): string {
    return FORMAT_TO_MIME[format] || 'image/jpeg'
  }

  /**
   * Gets the format from a mimetype.
   */
  static mimeToFormat(mimetype: string): string {
    return MIME_TO_FORMAT[mimetype] || 'jpg'
  }
}
